import { Box } from '@mui/material'
import usePreFilter from 'components/cell-visualizations/prefilter/usePreFilter'
import useFlagCondition from 'components/shared/useFlagCondition'
import useFlags from 'components/shared/useFlags'
import { useCallback, useMemo, useState } from 'react'
import { MorphotypePanelDisplayMode, PinnedMorphotype, PlotSelectionState } from 'redux/slices'
import { useNotificationSlice, usePlotRange, usePlotlySlice } from 'redux/slices/hooks'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useEventsManager from 'redux/slices/hooks/useEventsManager'
import { ScatterPlotData } from 'redux/slices/types'
import useDebouncedWindowWidth from 'utils/useDebouncedWindowWidth'
import OverlapWarningModal from '../cellSelectionDrawer/OverlapWarningModal'
import usePanelHeight from '../cellSelectionDrawer/usePanelHeight'
import { checkForOverlappingSelections, isSelectionValid } from '../utils'
import createMorphotypeFromClusterFromDataFrame from '../utils/createMorphotypeFromCluster'
import { BasePlot } from './BasePlot'
import {
  convertToDeepcellPlotMouseEvent,
  convertToDeepcellPlotSelectionEvent,
} from './convertToDeepcellEvent'
import { enhanceSelectionWithGrayCellsFromDataFrame } from './enhanceSelectionWithGrayCells'
import getMorphotypeSelectionInfo from './getMorphotypeSelectionInfo'
import GridTileOverlay from './GridTileOverlay'
import usePlotLayoutImages from './plotLayoutImages/usePlotLayoutImages'
import { useClusterOverlayTracesFromDataFrame } from './useClusterOverlayTraces'
import { useCustomAnnotation } from './useCustomAnnotation'
import { useDragHandlerAnnotations } from './useDragHandlerAnnotations'
import { useEmbeddingShapesAndAnnotations } from './useEmbeddingShapesAndAnnotations'
import { useHiddenBackgroundImages } from './useHiddenBackgroundImages'
import { useLeidenClusterCentroidsFromDataFrame } from './useLeidenClusterCentroids'
import { useRelayout } from './useRelayout'

type Props = ScatterPlotData & { isPreFilter?: boolean }

const ScatterPlot = (props: Props): JSX.Element => {
  const { plotData, scatterPlotMetaData, isPreFilter } = props

  const demoEnabled = useFlagCondition('demoEnabled')

  const { cellVisualizationSelfIntersectingPolygonMaxAreaThreshold } = useFlags()

  const { displayNotification } = useNotificationSlice()
  const eventsManager = useEventsManager()
  const {
    dataFrame,
    prefilteredCellIdSet,
    setPointsAsSelected,
    cellVisualizations: {
      pinnedCells,
      overlapWarningShown,
      highlightedLeidenCluster,
      cellInfoGroups,
    },
    visibleSelectedComparisonDimensions,
    // clearCellData,
    setMorphotypePanelDisplayMode,
    setOverlapWarningShown,
    addPinnedMorphotype,
    setShowMorphotypes,
    setHighlightedLeidenCluster,
  } = useCellVisualizationsSlice()

  const { plotly: { dragMode } } = usePlotlySlice()

  const { onPrefilterSelected, morphotypesToRemove, setMorphotypesToRemove } = usePreFilter()

  const { shapes: embeddingShapes, annotations: embeddingAnnotations } =
    useEmbeddingShapesAndAnnotations(scatterPlotMetaData)

  const { dragHandlerAnnotations } = useDragHandlerAnnotations(scatterPlotMetaData)

  // Check if we're in multi-plot mode (comparison dimensions visible)
  // Calculate this early so we can use mode-specific range below
  const isMultiPlotMode = (visibleSelectedComparisonDimensions?.length ?? 0) > 0

  // Get the comparison field (e.g., 'leiden', 'RUN_ID', 'SAMPLE_ID') for multi-plot mode
  const comparisonField = visibleSelectedComparisonDimensions?.[0]?.field

  // Get mode-specific range (single-plot or multi-plot)
  const { range } = usePlotRange(isMultiPlotMode)

  const { shapes: selectionShapes, shapeAnnotations: selectionAnnotations } = useMemo(
    () =>
      getMorphotypeSelectionInfo({
        morphotypes: pinnedCells,
        visibleDimensions: visibleSelectedComparisonDimensions,
        plotMetadata: scatterPlotMetaData,
        currentRange: range,
        cellInfo: undefined,
      }),
    [pinnedCells, visibleSelectedComparisonDimensions, scatterPlotMetaData, range]
  )

  // Show leiden cluster centroids when in Leiden Select mode (regardless of color-by setting)
  const isLeidenSelectMode = dragMode === 'Leiden Select'

  const plotPixelWidth = useDebouncedWindowWidth()

  // Calculate centroids for Leiden clusters and create (+) button trace
  // Also creates shapes and annotations that render ABOVE all traces and images
  // Hide + buttons in multi-plot mode to reduce clutter
  // Pass highlightedLeidenCluster to show hover region only on hovered cluster
  // Pass morphotypesToRemove to hide (+) buttons for already-added clusters in prefilter mode
  // Always use leiden cluster data for centroids (not the currently selected color-by group)
  // This ensures plus indicators appear regardless of which Color By mode is selected
  const { centroidTrace, centroidShapes, centroidAnnotations } =
    useLeidenClusterCentroidsFromDataFrame(
      dataFrame ?? null,
      cellInfoGroups?.leiden?.data,
      isLeidenSelectMode,
      isMultiPlotMode,
      range,
      plotPixelWidth,
      highlightedLeidenCluster,
      isPreFilter || false,
      morphotypesToRemove,
      prefilteredCellIdSet
    )

  const shapes = [...embeddingShapes, ...selectionShapes, ...centroidShapes]
  const annotations = [
    ...embeddingAnnotations,
    ...selectionAnnotations,
    ...dragHandlerAnnotations,
    ...centroidAnnotations,
  ]

  // Enhance plot data with size increase for highlighted cluster-based morphotypes and hovered Leiden clusters
  // Performance optimization: Modifies existing traces instead of adding duplicates
  // Use DataFrame to ensure leiden values are always available (memory-efficient)
  const plotDataWithClusterHighlights = useClusterOverlayTracesFromDataFrame(
    plotData,
    pinnedCells,
    highlightedLeidenCluster,
    dataFrame
  )

  // Add centroid trace with circular (+) buttons (renders on top as last trace)
  const plotDataWithCentroids = centroidTrace
    ? [...plotDataWithClusterHighlights, centroidTrace]
    : plotDataWithClusterHighlights

  // This boolean state is used to show the morphotype selection notification only once
  // for each time the user opens a session
  const [selectionNotificationDisplayed, setSelectionNotificationDisplayed] = useState(false)

  // Overlap warning modal state
  const [overlapWarningOpen, setOverlapWarningOpen] = useState(false)
  const [pendingSelection, setPendingSelection] = useState<PlotSelectionState | undefined>(
    undefined
  )
  const [overlappingMorphotypes, setOverlappingMorphotypes] = useState<PinnedMorphotype[]>([])

  const { CustomAnnotation, setAnnotationData } = useCustomAnnotation()

  const { onRelayout } = useRelayout(isMultiPlotMode)

  const handleRelayoutWithPrompt = (...params: Parameters<typeof onRelayout>) => {
    const { newRangeDetected } = onRelayout(...params)
    if (newRangeDetected) {
      // If the user zooms, close the annotation
      setAnnotationData()
    }
  }

  const hiddenBackgroundImages = useHiddenBackgroundImages()

  const cellImages = usePlotLayoutImages(isMultiPlotMode)

  const enhanceSelection = useCallback(
    (selection: PlotSelectionState): PlotSelectionState => {
      if (!dataFrame) return selection
      return enhanceSelectionWithGrayCellsFromDataFrame({
        selection,
        dataFrame,
        prefilteredCellIdSet,
        scatterPlotMetaData,
        visibleSelectedComparisonDimensions,
        isPreFilter: isPreFilter || false,
      })
    },
    [
      dataFrame,
      prefilteredCellIdSet,
      scatterPlotMetaData,
      visibleSelectedComparisonDimensions,
      isPreFilter,
    ]
  )

  /** Validates plot selection state
   * If the selection is invalid, will return undefined and show an error message
   */
  function validateSelection(e?: PlotSelectionState | undefined): PlotSelectionState | undefined {
    if (!e) {
      return undefined
    }

    // Allow selections with no visible points IF there's a selection shape (lasso/box)
    // This handles cases where:
    // - User selects only hidden/filtered cells (single plot mode)
    // - User selects only gray cells (multi-plot mode)
    // The enhancement step will add these hidden/gray cells to the selection
    const hasSelectionShape = Boolean(e.lassoPoints || e.range)
    if (e.points?.length === 0 && !hasSelectionShape) {
      // No points and no shape = user cleared the selection or clicked without dragging
      return undefined
    }

    const { lassoPoints } = e

    // Selection is a box selection -- no need to validate
    if (!lassoPoints) return e

    // Validate lasso selections
    if (!isSelectionValid(lassoPoints, cellVisualizationSelfIntersectingPolygonMaxAreaThreshold)) {
      const followUpAction = `Please try drawing your selection again${
        !isPreFilter ? ', or merge morphotypes to create more complex regions.' : '.'
      }`
      displayNotification({
        message: `Lasso selection boundaries cannot cross over themselves. ${followUpAction}`,
        type: 'error',
        showIcon: false,
        origin: { vertical: 'top', horizontal: 'center' },
        timeout: 10000,
      })
      setPointsAsSelected(undefined)
      throw new Error('Invalid selection')
    }

    // Check for overlaps with existing selections
    const overlapResult = checkForOverlappingSelections(lassoPoints, pinnedCells || [])
    if (overlapResult.overlappingMorphotypes.length > 0) {
      // Store the pending selection
      setPendingSelection(e)
      setOverlappingMorphotypes(overlapResult.overlappingMorphotypes)

      // Only show warning modal if it hasn't been shown this session
      if (!overlapWarningShown) {
        setOverlapWarningOpen(true)
        setOverlapWarningShown(true)
        return undefined // Don't proceed with selection yet
      }
      // If warning already shown, proceed with selection but highlight the overlap
    }

    return e
  }

  const onSelected = (e?: PlotSelectionState | undefined) => {
    // Enhance selection with gray cells if in multi-plot mode
    const enhancedSelection = e ? enhanceSelection(e) : undefined

    // If the enhanced selection has no points, treat it as undefined
    const finalSelection = enhancedSelection?.points?.length ? enhancedSelection : undefined

    setPointsAsSelected(finalSelection)
    if (finalSelection) {
      setMorphotypePanelDisplayMode(MorphotypePanelDisplayMode.CELL_GROUPS)
    }

    // If the selection is valid, display a notification to the user if it's their
    // first time drawing a morphotype to let them know what they've done
    if (!selectionNotificationDisplayed && !demoEnabled && finalSelection) {
      displayNotification({
        message: 'Your selection has been saved as a morphotype',
        type: 'success',
        showIcon: false,
        origin: { vertical: 'bottom', horizontal: 'center' },
        timeout: 5000,
      })

      // Mark that we've shown the notification once this session
      // And don't show it again
      setSelectionNotificationDisplayed(true)
    }
  }

  // Handle overlap warning modal actions
  const handleOverlapProceed = () => {
    if (pendingSelection) {
      // Enhance pending selection with gray cells too
      const enhancedPendingSelection = enhanceSelection(pendingSelection)
      setPointsAsSelected(enhancedPendingSelection)
      if (enhancedPendingSelection) {
        setMorphotypePanelDisplayMode(MorphotypePanelDisplayMode.CELL_GROUPS)
      }
    }
    setOverlapWarningOpen(false)
    setPendingSelection(undefined)
  }

  const handleOverlapCancel = () => {
    setOverlapWarningOpen(false)
    setPendingSelection(undefined)
    setPointsAsSelected(undefined)
  }

  // Handle hover to show pointer cursor over centroid buttons and trigger hover region
  const handlePlotHover = useCallback(
    (e: Plotly.PlotMouseEvent) => {
      // Check if hovering over a centroid marker (+ button)
      if (e.points && e.points.length > 0) {
        const point = e.points[0]

        // Access the trace data to get cellIds
        if ('data' in point && point.data && 'cellIds' in point.data) {
          const traceData = point.data as { cellIds?: string[] }
          const pointIndex = point.pointIndex as number
          const cellId = traceData.cellIds?.[pointIndex]

          // Check if this is a centroid marker
          if (cellId && typeof cellId === 'string' && cellId.startsWith('cluster_centroid_')) {
            // Extract cluster value from cellId (e.g., "cluster_centroid_1" -> "1")
            const clusterValue = cellId.replace('cluster_centroid_', '')

            // Set highlighted cluster to show hover region
            setHighlightedLeidenCluster(clusterValue)

            // Change cursor to pointer
            const plotDiv = e.event.currentTarget as HTMLElement
            if (plotDiv) {
              const dragLayer = plotDiv.querySelector('.nsewdrag') as HTMLElement
              if (dragLayer) {
                dragLayer.style.cursor = 'pointer'
              }
              plotDiv.style.cursor = 'pointer'
            }
            return
          }
        }
      }

      // Reset cursor and clear highlight for non-centroid hovers
      setHighlightedLeidenCluster(undefined)
      const plotDiv = e.event?.currentTarget as HTMLElement
      if (plotDiv) {
        const dragLayer = plotDiv.querySelector('.nsewdrag') as HTMLElement
        if (dragLayer) {
          dragLayer.style.cursor = ''
        }
        plotDiv.style.cursor = ''
      }
    },
    [setHighlightedLeidenCluster]
  )

  const handlePlotClick = useCallback(
    (e: Plotly.PlotMouseEvent) => {
      // Check if user clicked on a centroid marker (+ button)
      if (e.points && e.points.length > 0) {
        const point = e.points[0]

        // Access the trace data to get cellIds
        if ('data' in point && point.data && 'cellIds' in point.data) {
          const traceData = point.data as { cellIds?: string[] }
          const pointIndex = point.pointIndex as number
          const cellId = traceData.cellIds?.[pointIndex]

          // Check if this is a centroid marker click
          if (cellId && typeof cellId === 'string' && cellId.startsWith('cluster_centroid_')) {
            const clusterValue = cellId.replace('cluster_centroid_', '')

            // Prevent default plot interaction
            e.event.stopPropagation()

            // Determine which morphotype list to use based on mode
            const existingMorphotypes = isPreFilter ? morphotypesToRemove || [] : pinnedCells || []

            // Create morphotype from cluster using DataFrame (memory-efficient)
            if (!dataFrame) {
              displayNotification({
                message: 'DataFrame not available',
                type: 'error',
                showIcon: false,
                origin: { vertical: 'bottom', horizontal: 'center' },
                timeout: 5000,
              })
              return
            }

            const newMorphotype = createMorphotypeFromClusterFromDataFrame({
              clusterValue,
              dataFrame,
              existingMorphotypes,
              prefilteredCellIdSet,
            })

            if (!newMorphotype) {
              displayNotification({
                message: `Failed to create morphotype from Leiden Cluster ${clusterValue}`,
                type: 'error',
                showIcon: false,
                origin: { vertical: 'bottom', horizontal: 'center' },
                timeout: 5000,
              })
              return
            }

            // Add the morphotype to the appropriate store
            if (isPreFilter) {
              // In pre-filter mode, add to morphotypes to remove
              setMorphotypesToRemove([newMorphotype, ...(morphotypesToRemove || [])])
            } else {
              // In normal mode, add to pinned morphotypes
              addPinnedMorphotype(newMorphotype)
              // Show the morphotypes drawer
              setShowMorphotypes(true)
              // Track cluster selection analytics
              eventsManager.sendSelectClusterEvent(
                newMorphotype.cells.points?.length ?? 0,
                clusterValue,
                'plot'
              )
            }

            // Show notification to user
            const actionText = isPreFilter ? 'exclusion filter' : 'morphotype'
            displayNotification({
              message: `${
                actionText.charAt(0).toUpperCase() + actionText.slice(1)
              } created from Leiden Cluster ${clusterValue}`,
              type: 'success',
              showIcon: false,
              origin: { vertical: 'bottom', horizontal: 'center' },
              timeout: 5000,
            })

            return
          }
        }
      }

      // Handle regular plot interactions
      setAnnotationData(convertToDeepcellPlotMouseEvent(e))
    },
    [
      dataFrame,
      prefilteredCellIdSet,
      pinnedCells,
      morphotypesToRemove,
      isPreFilter,
      addPinnedMorphotype,
      setMorphotypesToRemove,
      setShowMorphotypes,
      displayNotification,
      setAnnotationData,
      eventsManager,
    ]
  )

  const commonPlotProps: Parameters<typeof BasePlot>[0] = {
    onClick: handlePlotClick,
    onHover: handlePlotHover,
  }

  const mainPlotProps: Parameters<typeof BasePlot>[0] = {
    ...commonPlotProps,
    shapes,
    annotations,
    onRelayout: handleRelayoutWithPrompt,
    onSelected: (e) => onSelected(validateSelection(convertToDeepcellPlotSelectionEvent(e))),
  }

  const { shapes: preFilterShapes } = useMemo(
    () => getMorphotypeSelectionInfo({ morphotypes: morphotypesToRemove, cellInfo: undefined }),
    [morphotypesToRemove]
  )

  const preFilterPlotProps: Parameters<typeof BasePlot>[0] = {
    ...commonPlotProps,
    shapes: [...preFilterShapes, ...centroidShapes],
    annotations: centroidAnnotations,
    onRelayout,
    onSelected: (e) =>
      onPrefilterSelected(validateSelection(convertToDeepcellPlotSelectionEvent(e))),
  }

  const propsToUse = isPreFilter ? preFilterPlotProps : mainPlotProps

  const { height: plotHeight } = usePanelHeight(0)

  // useUnmount(clearCellData)

  return (
    <Box sx={{ position: 'relative' }}>
      <BasePlot
        {...propsToUse}
        plotData={plotDataWithCentroids}
        images={[...cellImages, ...hiddenBackgroundImages]}
        isMultiPlotMode={isMultiPlotMode}
        comparisonField={comparisonField}
      />
      {/* Add grid tile overlay only for main plot with comparison dimensions */}
      {!isPreFilter && visibleSelectedComparisonDimensions?.length && (
        <GridTileOverlay scatterPlotMetaData={scatterPlotMetaData} plotHeight={plotHeight} plotData={plotDataWithCentroids} />
      )}
      <CustomAnnotation />

      {/* Overlap Warning Modal */}
      <OverlapWarningModal
        open={overlapWarningOpen}
        onClose={() => setOverlapWarningOpen(false)}
        onProceed={handleOverlapProceed}
        onCancel={handleOverlapCancel}
        overlappingMorphotypes={overlappingMorphotypes}
      />
    </Box>
  )
}

export default ScatterPlot
